/**
 * Pure lesson logic: judging an answer, walking the hint ladder, deciding mastery. No DOM here, so it is all tested.
 */
import { DEFAULT_MASTERY, type Item, type MasteryRule, type Misconception } from "./types";

/** "1,200" → "1200", "  550 " → "550", "Round UP" → "round up". */
export const normalize = (s: string): string =>
  s.trim().toLowerCase().replace(/[x*]/g, "×").replace(/-/g, "−").replace(/\//g, "÷").replace(/[\s,]+/g, " ").replace(/(\d) (?=\d)/g, "$1")
    .replace(/ ?([+×÷=−]) ?/g, "$1").replace(/([([]) /g, "$1").replace(/ ([)\]])/g, "$1");

export const isRight = (item: Item, raw: string): boolean => {
  const n = normalize(raw);
  if (!n) return false;
  return [item.answer, ...(item.alsoAccept ?? [])].some((a) => normalize(a) === n);
};

export interface Response {
  correct: boolean;
  /** What to show. On a correct answer: the reveal (or a plain acknowledgment). */
  text: string;
  /** The misconception that matched, for the data. */
  misconception?: Misconception;
  /** Which ladder rung was shown (0 = none). */
  hintLevel: number;
  /** The ladder is used up: show the way and move on to a fresh problem. */
  showWay: boolean;
}

/** Plain acknowledgments. No exclamation-point praise; the reveal carries the weight. */
const OK = ["That's it.", "Yes.", "Right.", "Correct."];

/**
 * Respond to one try. `tries` counts the wrong tries already made on this item.
 * A matching misconception is said first; the ladder advances only when no specific response fits.
 */
export function respond(item: Item, raw: string, tries: number, rng: () => number = Math.random): Response {
  if (isRight(item, raw)) {
    return { correct: true, text: item.reveal ?? OK[Math.floor(rng() * OK.length)]!, hintLevel: 0, showWay: false };
  }
  const m = item.diagnose?.(raw) ?? null;
  const level = Math.min(tries + 1, item.hints.length);
  const showWay = tries + 1 >= item.hints.length + 1; // one more try after the last rung, then the way
  if (m && !showWay) return { correct: false, text: `Not yet. ${m.hint}`, misconception: m, hintLevel: level, showWay: false };
  if (showWay) return { correct: false, text: item.hints[item.hints.length - 1] ?? "", misconception: m ?? undefined, hintLevel: item.hints.length, showWay: true };
  return { correct: false, text: `Not yet. ${item.hints[level - 1] ?? ""}`.trim(), hintLevel: level, showWay: false };
}

export interface BlockState {
  /** First-try results, oldest first. */
  results: boolean[];
  done: boolean;
  mastered: boolean;
  /** True once the student has missed 3 of the last 4: send them back to the discovery (or the skill underneath). */
  needsBackup: boolean;
}

export const newBlock = (): BlockState => ({ results: [], done: false, mastered: false, needsBackup: false });

export const rule = (r?: Partial<MasteryRule>): MasteryRule => ({ ...DEFAULT_MASTERY, ...r });

/** Record one finished problem (firstTry = right with no hints). */
export function recordResult(b: BlockState, firstTry: boolean, r: MasteryRule): BlockState {
  const results = [...b.results, firstTry];
  const last = results.slice(-r.window);
  const mastered = results.length >= r.min && last.filter(Boolean).length >= Math.min(r.need, last.length) && last.length >= Math.min(r.window, r.min);
  const recent = results.slice(-4);
  const needsBackup = !mastered && recent.length === 4 && recent.filter((x) => !x).length >= 3;
  const done = mastered || results.length >= r.max;
  return { results, done, mastered, needsBackup };
}

/** The level a generator should use: climbs with a run of first-try answers, drops after a miss. */
export function levelFor(b: BlockState): number {
  let streak = 0;
  for (let i = b.results.length - 1; i >= 0 && b.results[i]; i--) streak++;
  return Math.min(3, Math.floor(streak / 2));
}

/** Seeded random (mulberry32) so tests and replays are deterministic. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const int = (rng: () => number, lo: number, hi: number): number => lo + Math.floor(rng() * (hi - lo + 1));
export const pick = <T>(rng: () => number, xs: readonly T[]): T => xs[Math.floor(rng() * xs.length)]!;
